
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  ActionableHelperView,
  HelperView,
  Pagination,
  Table,
  TableHeaderCell,
  TableCell,
  BaseIcon
} from '@warrenbrasil/nebraska-web';
import { WarrenModules } from '@/types/navigation';
import {
  GetRangedPagedTransactionsQuery,
  GetRangedPagedTransactionsDocument,
  GlobalTransaction,
  EGlobalTransactionOrder,
  EGlobalTransactionOrderBy
} from '@/modules/common/services/graphql/server/server.sdk';
import { GlobalTransactionAmount } from '@/modules/transaction/components/GlobalTransactionAmount';
import { GlobalTransactionStatus } from '@/modules/transaction/components/GlobalTransactionStatus';
import { TransactionTransfer } from './components/TransactionTransfer';
import { TransactionsTableMobile } from './components/TransactionsTableMobile';
import { TransactionsFilters } from './components/TransactionsFilters';
import { trackChangePaginationClick } from './track-events';
import { getTransactionsReport } from '@/modules/transaction/services/banking';
import { forceFileDownload } from '@/modules/common/helpers/force-download';

import {
  TableColumn,
  TransactionRow,
  SortingOrder,
  TransactionFilters,
  TransactionFiltersOptions
} from './types';
import { parseTransaction } from './helpers';
import { OpenTransactionEventPayload } from '../../types';
import { Route } from 'vue-router';
import { pickBy as getNonEmptyProperties } from 'lodash-es';

const columns: TableColumn[] = [
  { key: EGlobalTransactionOrderBy.TransactionDate, title: 'Data' },
  {
    component: 'TransactionTransfer',
    props: { from: 'Origem', to: 'Destino' }
  },
  {
    title: 'Valor',
    class: 'transactions-table__cell--align-right',
    width: '100px'
  },
  { title: 'Status', class: 'transaction__status-header' }
];

@Component({
  components: {
    ActionableHelperView,
    HelperView,
    Pagination,
    Table,
    TableHeaderCell,
    TableCell,
    BaseIcon,
    GlobalTransactionAmount,
    GlobalTransactionStatus,
    TransactionTransfer,
    TransactionsTableMobile,
    TransactionsFilters
  }
})
export default class TransactionsTable extends Vue {
  @Prop({ type: String, required: false })
  readonly module!: WarrenModules;

  @Prop({ type: Boolean, required: false })
  readonly reload?: boolean;

  private sortingColumn = EGlobalTransactionOrderBy.LiquidateAndCreatedDate;
  sortingOrder: SortingOrder = SortingOrder.Descending;
  pageIndex = 1;
  pageSize = 9;
  totalPages = 1;
  transactions: TransactionRow[] = [];
  hasError = false;
  filterOptions: Reactive<TransactionFiltersOptions> = null;
  isFetchingTransactions = false;
  isFetchingDownload = false;

  created() {
    this.listTransactions();
  }

  @Emit()
  openTransactionModal(
    transaction: GlobalTransaction
  ): OpenTransactionEventPayload {
    return {
      id: transaction.id,
      type: transaction.type?.value!,
      typeV2: transaction.typeV2?.value!
    };
  }

  @Watch('reload')
  private reloadTable() {
    this.listTransactions();
  }

  get computedColumns(): TableColumn[] {
    return columns.map(column => ({
      ...column,
      isSorting: 'key' in column ? column.key === this.sortingColumn : false,
      sortingOrder: this.sortingOrder
    }));
  }

  async listTransactions(page = this.pageIndex) {
    this.isFetchingTransactions = true;
    this.hasError = false;

    try {
      const { data } =
        await this.$apollo.query<GetRangedPagedTransactionsQuery>({
          query: GetRangedPagedTransactionsDocument,
          variables: {
            pageSize: this.pageSize,
            pageIndex: page,
            order: this.mapSortingOrder(this.sortingOrder),
            orderBy: this.sortingColumn,
            module: this.module,
            withPortfolios: this.module === WarrenModules.Wealth,
            ...getNonEmptyProperties(this.filters)
          }
        });
      this.setTransactionsData(data);
    } catch {
      this.hasError = true;
    } finally {
      this.isFetchingTransactions = false;
    }
  }

  private setTransactionsData({
    transaction,
    wealth
  }: GetRangedPagedTransactionsQuery) {
    const { page, transactions, filters } = transaction!.rangedPagedList!;
    this.pageIndex = page.index;
    this.totalPages = page.amount;
    this.transactions = transactions.map(parseTransaction);
    this.filterOptions = {
      datePeriods: filters.datePeriods,
      movementTypes: filters.movementTypes,
      portfolios: wealth?.portfolios ?? []
    };
  }

  // Table pagination and sorting

  changeSorting(
    columnKey: EGlobalTransactionOrderBy,
    sortingOrder: SortingOrder
  ) {
    this.sortingColumn = columnKey;
    this.sortingOrder = sortingOrder;
    this.listTransactions(1);
  }

  pageChanged(page: number) {
    this.listTransactions(page);

    trackChangePaginationClick(page);
  }

  private mapSortingOrder(tableSortingOrder: SortingOrder) {
    return tableSortingOrder === SortingOrder.Ascending
      ? EGlobalTransactionOrder.Asc
      : EGlobalTransactionOrder.Desc;
  }

  get showPagination() {
    return !this.isFetchingTransactions && this.totalPages > 1;
  }

  get showEmptyTableMessage() {
    return !this.isFetchingTransactions && this.transactions.length === 0;
  }

  getTransactionSource(transaction: TransactionRow) {
    if (this.module === WarrenModules.Wealth) {
      return transaction.from?.subtitle || transaction.from?.title;
    }
    return transaction.from?.title || transaction.from?.subtitle;
  }

  // Table filtering

  filterTransactions(filters: TransactionFilters) {
    this.filters = filters;
    this.listTransactions(1);
  }

  set filters(filters: TransactionFilters) {
    const query = getNonEmptyProperties({
      ...this.$route.query,
      ...filters
    }) as Route['query'];
    this.$router.replace({ name: this.$route.name!, query });
  }

  get filters(): TransactionFilters {
    const filters = {
      portfolioId: this.$route.query.portfolioId || '',
      datePeriod: this.$route.query.datePeriod || '',
      movementType: this.$route.query.movementType || ''
    } as TransactionFilters;
    return filters;
  }

  async statementDownload(filters: TransactionFilters) {
    this.isFetchingDownload = true;
    try {
      const data = await getTransactionsReport(filters);
      forceFileDownload({
        data,
        type: 'application/pdf',
        fileName: `Warren_ExtratoConta.pdf`
      });
    } finally {
      this.isFetchingDownload = false;
    }
  }
}
